import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sparkles,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Check,
  Ban,
  AlertCircle,
  Info,
} from 'lucide-react';
import { aiBuildPreviewStream, aiBuildCommit } from '../../api';
import type { AiBuildCommitAssignment } from '../../api';
import type { AiBuildProposal, AiBuildSummary, AiBuildProgress } from '../../types';
import { DRIVER_CONFIG } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import '../../aiIdentity.css';

interface AiBuildReviewScreenProps {
  versionId: number;
  currency?: string | null;
  // Budget versions get budget wording ("Drafting your budget", etc.)
  isBudget?: boolean;
  onClose: () => void;
  onCommitted: (batchId: string, updated: number) => void;
}

type Phase = 'loading' | 'review' | 'committing' | 'error';

interface RowState {
  accepted: boolean;
  editedInput: string | null; // null = untouched, keep suggestion.input
}

// Percent-style drivers show their input as "N%", everything else as currency
function isPercentDriver(driverType: string): boolean {
  return driverType.includes('%');
}

function confidenceBadge(proposal: AiBuildProposal): { label: string; className: string } {
  const { classification, correlation } = proposal.suggestion;
  if (classification === 'fixed') {
    return { label: 'Fixed cost', className: 'bg-indigo-50 text-indigo-700' };
  }
  if (typeof correlation === 'number' && correlation >= 0.8) {
    return { label: 'High', className: 'bg-emerald-50 text-emerald-700' };
  }
  if (typeof correlation === 'number' && correlation >= 0.5) {
    return { label: 'Medium', className: 'bg-amber-50 text-amber-700' };
  }
  return { label: 'Heuristic', className: 'bg-slate-100 text-slate-600' };
}

export function AiBuildReviewScreen({ versionId, currency = 'USD', isBudget = false, onClose, onCommitted }: AiBuildReviewScreenProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [progress, setProgress] = useState<AiBuildProgress>({ stage: 'analyzing' });
  const [proposals, setProposals] = useState<AiBuildProposal[]>([]);
  const [summary, setSummary] = useState<AiBuildSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Per-row accept/reject + edited input state, keyed by chartOfAccountId
  const [rowState, setRowState] = useState<Record<number, RowState>>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [commitError, setCommitError] = useState<string | null>(null);

  // Lock body scroll while the takeover is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Start the SSE preview stream on mount (abort on unmount).
  // `attempt` allows the error state's "Try again" to restart the stream.
  useEffect(() => {
    setPhase('loading');
    setProgress({ stage: 'analyzing' });
    setError(null);

    const abort = aiBuildPreviewStream({
      versionId,
      onProgress: (p) => setProgress(p),
      onComplete: (result) => {
        setProposals(result.proposals);
        setSummary(result.summary);
        // Every proposal starts accepted with the suggested input
        const initial: Record<number, RowState> = {};
        for (const p of result.proposals) {
          initial[p.chartOfAccountId] = { accepted: true, editedInput: null };
        }
        setRowState(initial);
        setPhase('review');
      },
      onError: (err) => {
        setError(err);
        setPhase('error');
      },
    });
    return abort;
  }, [versionId, attempt]);

  // Group proposals by department (stable order: department name asc, then account number)
  const groups = useMemo(() => {
    const map = new Map<string, AiBuildProposal[]>();
    for (const p of proposals) {
      // Trim: COA dept strings arrive with trailing spaces, which would
      // otherwise split one department into two groups
      const dept = (p.department || '').trim() || 'Other';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept)!.push(p);
    }
    const entries = Array.from(map.entries());
    entries.sort((a, b) => a[0].localeCompare(b[0]));
    for (const [, items] of entries) {
      items.sort((a, b) => String(a.accountNumber || '').localeCompare(String(b.accountNumber || ''), undefined, { numeric: true }));
    }
    return entries;
  }, [proposals]);

  // Driver mix for the summary strip (top 6 by count, stable order)
  const driverMix = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of proposals) {
      counts.set(p.suggestion.driverType, (counts.get(p.suggestion.driverType) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [proposals]);

  // Proposals the LLM arbiter changed (reason carries the "AI review:" prefix)
  const aiReviewedCount = useMemo(
    () => proposals.filter(p => p.suggestion.reason?.startsWith('AI review:')).length,
    [proposals]
  );

  const acceptedCount = useMemo(
    () => proposals.filter(p => rowState[p.chartOfAccountId]?.accepted).length,
    [proposals, rowState]
  );
  const rejectedCount = proposals.length - acceptedCount;
  const acceptedAnnualTotal = useMemo(
    () => proposals.reduce((sum, p) => sum + (rowState[p.chartOfAccountId]?.accepted ? (p.annualTotal || 0) : 0), 0),
    [proposals, rowState]
  );

  const setAccepted = useCallback((chartOfAccountId: number, accepted: boolean) => {
    setRowState(prev => ({
      ...prev,
      [chartOfAccountId]: { ...(prev[chartOfAccountId] || { accepted: true, editedInput: null }), accepted },
    }));
  }, []);

  const setGroupAccepted = useCallback((items: AiBuildProposal[], accepted: boolean) => {
    setRowState(prev => {
      const next = { ...prev };
      for (const p of items) {
        next[p.chartOfAccountId] = { ...(next[p.chartOfAccountId] || { accepted: true, editedInput: null }), accepted };
      }
      return next;
    });
  }, []);

  const setEditedInput = useCallback((chartOfAccountId: number, value: string) => {
    setRowState(prev => ({
      ...prev,
      [chartOfAccountId]: { ...(prev[chartOfAccountId] || { accepted: true, editedInput: null }), editedInput: value },
    }));
  }, []);

  const toggleRowExpanded = useCallback((chartOfAccountId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(chartOfAccountId)) next.delete(chartOfAccountId);
      else next.add(chartOfAccountId);
      return next;
    });
  }, []);

  const toggleGroupCollapsed = useCallback((dept: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  }, []);

  // Effective driver input for a row: edited value if valid, otherwise the suggestion
  const effectiveInput = useCallback((p: AiBuildProposal): number | null => {
    const state = rowState[p.chartOfAccountId];
    if (state?.editedInput !== null && state?.editedInput !== undefined && state.editedInput !== '') {
      const parsed = parseFloat(state.editedInput);
      if (Number.isFinite(parsed)) return parsed;
    }
    return p.suggestion.input;
  }, [rowState]);

  const handleApply = useCallback(async () => {
    const accepted = proposals.filter(p => rowState[p.chartOfAccountId]?.accepted);
    if (accepted.length === 0) return;

    const assignments: AiBuildCommitAssignment[] = accepted.map(p => ({
      chartOfAccountId: p.chartOfAccountId,
      driverType: p.suggestion.driverType,
      driverInput: effectiveInput(p),
      inputMode: 'monthly',
      effectiveStartMonth: 1,
      effectiveEndMonth: 12,
      selectedDepartments: p.suggestion.selectedDepartments ?? null,
      sourceChartOfAccountIds: null,
      fallbackDriverType: null,
      laborPositionConfigs: p.suggestion.laborPositionConfigs ?? null,
    }));

    setPhase('committing');
    setCommitError(null);
    try {
      // Budget-mode: the proposed drivers were calculated against the seeded
      // rooms baseline — commit it with them or $/ORN drivers land on 0 rooms.
      const result = await aiBuildCommit({ versionId, assignments, roomStatSeed: summary?.roomStatSeed ?? null });
      onCommitted(result.batchId, result.updated);
    } catch (err) {
      console.error('Failed to commit AI build assignments:', err);
      setCommitError(err instanceof Error ? err.message : 'Failed to apply driver assignments');
      setPhase('review');
    }
  }, [proposals, rowState, effectiveInput, versionId, onCommitted]);

  const isCommitting = phase === 'committing';

  return (
    <div
      className="flex flex-col"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--lodge-onyx-50)' }}
    >
      {/* ── Header ── */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b"
        style={{ background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))', borderColor: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="lodge-ai-tile w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight" style={{ color: 'var(--lodge-onyx-800)' }}>
              Build with AI
            </h2>
            <p className="text-xs leading-tight" style={{ color: 'var(--lodge-onyx-500)' }}>
              {phase === 'review' || phase === 'committing'
                ? 'Review the proposed drivers, then apply the ones you accept'
                : `AI-proposed ${isBudget ? 'budget' : 'forecast'} drivers for every account`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {(phase === 'review' || phase === 'committing') && (
            <>
              <div className="hidden sm:flex items-center gap-3 text-xs" style={{ color: 'var(--lodge-onyx-500)' }}>
                <span>
                  <span className="font-semibold tabular-nums" style={{ color: 'var(--lodge-onyx-800)' }}>{acceptedCount}</span> accepted
                </span>
                <span>
                  <span className="font-semibold tabular-nums" style={{ color: 'var(--lodge-onyx-800)' }}>{rejectedCount}</span> rejected
                </span>
                <span className="hidden md:inline">
                  Accepted total:{' '}
                  <span className="font-semibold tabular-nums" style={{ color: 'var(--lodge-onyx-800)' }}>
                    {formatCurrency(acceptedAnnualTotal, true, currency)}
                  </span>
                </span>
              </div>
              <button
                onClick={handleApply}
                disabled={acceptedCount === 0 || isCommitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)', border: 'none', boxShadow: 'var(--shadow-orange)' }}
                onMouseEnter={e => { if (acceptedCount > 0 && !isCommitting) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
              >
                {isCommitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Applying…
                  </>
                ) : (
                  <>
                    <Check size={15} />
                    Apply {acceptedCount > 0 ? acceptedCount : ''} {acceptedCount === 1 ? 'driver' : 'drivers'}
                  </>
                )}
              </button>
            </>
          )}
          <button
            onClick={onClose}
            disabled={isCommitting}
            className="p-2 rounded-lg transition-colors disabled:opacity-40"
            style={{ color: 'var(--lodge-onyx-400)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* ── Commit error banner ── */}
      {commitError && (
        <div
          className="flex-shrink-0 flex items-center gap-2 px-5 py-2 text-sm border-b"
          style={{ background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }}
        >
          <AlertCircle size={15} className="flex-shrink-0" />
          <span className="min-w-0">{commitError}</span>
          <button
            onClick={() => setCommitError(null)}
            className="ml-auto p-1 rounded flex-shrink-0"
            style={{ color: '#b91c1c' }}
            title="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Body ── */}
      {phase === 'loading' && (() => {
        // Staged narrative — makes the engine's work legible instead of a
        // generic spinner. SSE stages: analyzing → (arbitrating) → calculating.
        const stageOrder = ['analyzing', 'arbitrating', 'calculating'];
        const currentIdx = Math.max(0, stageOrder.indexOf(progress.stage || 'analyzing'));
        const steps = [
          { key: 'analyzing', label: 'Reading 12 months of account history' },
          { key: 'arbitrating', label: 'Choosing the best driver for each account' },
          { key: 'calculating', label: progress.stage === 'calculating' && progress.accountCount ? `Projecting monthly values for ${progress.accountCount} accounts` : 'Projecting 12 months of values' },
        ];
        return (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="rounded-2xl px-8 py-9 max-w-sm w-full mx-4"
              style={{ background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))', boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0,0,0,.1))' }}
            >
              <div className="lodge-ai-tile lodge-ai-thinking mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-5">
                <Sparkles size={22} />
              </div>
              <h3 className="text-sm font-semibold mb-4 text-center" style={{ color: 'var(--lodge-onyx-800)' }}>
                Drafting your {isBudget ? 'budget' : 'forecast'}
              </h3>
              <div className="space-y-2.5">
                {steps.map((step, i) => {
                  // Collapse the arbitrating step position when the stream skips it
                  const stepIdx = stageOrder.indexOf(step.key);
                  const done = stepIdx < currentIdx;
                  const active = stepIdx === currentIdx;
                  return (
                    <div
                      key={step.key}
                      className="lodge-ai-step flex items-center gap-2.5 text-xs"
                      style={{
                        color: done || active ? 'var(--lodge-onyx-700, var(--fv3-n-700, #334155))' : 'var(--lodge-onyx-400, var(--fv3-n-400, #94a3b8))',
                        opacity: done || active ? 1 : 0.65,
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        {done ? (
                          <Check size={13} className="lodge-ai-step-check" style={{ color: 'var(--lodge-orange-500)' }} />
                        ) : active ? (
                          <span className="lodge-ai-step-dot w-2 h-2 rounded-full" style={{ background: 'var(--lodge-orange-400)' }} />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lodge-onyx-200, var(--fv3-n-100, #e2e8f0))' }} />
                        )}
                      </span>
                      {step.label}
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] mt-5 text-center" style={{ color: 'var(--lodge-onyx-400)' }}>
                Nothing is saved until you review and apply
              </p>
            </div>
          </div>
        );
      })()}

      {phase === 'error' && (
        <div className="flex-1 flex items-center justify-center">
          <div
            className="rounded-xl px-8 py-10 text-center max-w-md mx-4"
            style={{ background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))', boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0,0,0,.1))' }}
          >
            <div className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#fef2f2' }}>
              <AlertCircle size={24} style={{ color: '#dc2626' }} />
            </div>
            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--lodge-onyx-800)' }}>
              AI build failed
            </h3>
            <p className="text-xs mb-5 break-words" style={{ color: 'var(--lodge-onyx-500)' }}>
              {error || 'Something went wrong while analyzing this version.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                style={{ color: 'var(--lodge-onyx-600)', border: '1px solid var(--lodge-onyx-200, var(--fv3-n-200, #cbd5e1))' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                Close
              </button>
              <button
                onClick={() => setAttempt(a => a + 1)}
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
                style={{ background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)', border: 'none', boxShadow: 'var(--shadow-orange)' }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)')}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)')}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {(phase === 'review' || phase === 'committing') && (
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="max-w-5xl mx-auto space-y-3">
            {/* Comprehension layer: what did the AI just do, in 3 seconds */}
            <div className="lodge-ai-reveal flex items-center gap-2 flex-wrap px-1 pb-1">
              <span className="text-xs font-semibold" style={{ color: 'var(--lodge-onyx-600)' }}>
                {proposals.length} drivers proposed
              </span>
              <span className="w-px h-3.5" style={{ background: 'var(--lodge-onyx-200, var(--fv3-n-100, #e2e8f0))' }} />
              {driverMix.map(([driver, count]) => (
                <span key={driver} className="lodge-ai-chip">
                  {DRIVER_CONFIG[driver]?.label ?? driver}
                  <span className="lodge-ai-chip-count">{count}</span>
                </span>
              ))}
              {aiReviewedCount > 0 && (
                <span className="lodge-ai-chip" style={{ borderColor: 'var(--lodge-orange-200, var(--fv3-accent-line, #fed7aa))', color: 'var(--lodge-orange-600, var(--fv3-accent-ink, #ea580c))' }}>
                  <Sparkles size={11} />
                  AI reviewed
                  <span className="lodge-ai-chip-count" style={{ color: 'var(--lodge-orange-600, var(--fv3-accent-ink, #ea580c))' }}>{aiReviewedCount}</span>
                </span>
              )}
              {summary?.roomStatSeed && summary.roomStatSeed.seededMonths > 0 && (
                <span className="lodge-ai-chip" title={`Monthly occupied/available room nights seeded from ${summary.roomStatSeed.baseYear} (PMS actuals, then ${summary.roomStatSeed.baseYear} budget). Applied as room-stat overrides on apply — editable on the Occupancy row.`}>
                  Rooms baseline from {summary.roomStatSeed.baseYear}
                  <span className="lodge-ai-chip-count">{summary.roomStatSeed.seededMonths} mo</span>
                </span>
              )}
            </div>

            {groups.map(([dept, items], groupIdx) => {
              const isCollapsed = collapsedGroups.has(dept);
              const groupAcceptedCount = items.filter(p => rowState[p.chartOfAccountId]?.accepted).length;
              const groupAcceptedTotal = items.reduce(
                (sum, p) => sum + (rowState[p.chartOfAccountId]?.accepted ? (p.annualTotal || 0) : 0),
                0
              );
              const allAccepted = groupAcceptedCount === items.length;
              const noneAccepted = groupAcceptedCount === 0;

              return (
                <div
                  key={dept}
                  className="lodge-ai-reveal rounded-xl overflow-hidden border"
                  style={{
                    background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))',
                    borderColor: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))',
                    animationDelay: `${Math.min(groupIdx * 45, 360)}ms`,
                  }}
                >
                  {/* Group header */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 border-b"
                    style={{ background: 'var(--lodge-onyx-50)', borderColor: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))' }}
                  >
                    <button
                      onClick={() => toggleGroupCollapsed(dept)}
                      className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
                      style={{ color: 'var(--lodge-onyx-800)' }}
                    >
                      {isCollapsed ? <ChevronRight size={15} className="flex-shrink-0" /> : <ChevronDown size={15} className="flex-shrink-0" />}
                      <span className="text-sm font-semibold truncate">{dept}</span>
                      <span className="text-xs flex-shrink-0" style={{ color: 'var(--lodge-onyx-500)' }}>
                        {groupAcceptedCount}/{items.length} accepted
                      </span>
                    </button>
                    <span className="text-sm font-semibold tabular-nums flex-shrink-0" style={{ color: 'var(--lodge-onyx-800)' }}>
                      {formatCurrency(groupAcceptedTotal, true, currency)}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => setGroupAccepted(items, true)}
                        disabled={allAccepted || isCommitting}
                        className="px-2 py-1 text-[11px] font-semibold rounded-md transition-colors disabled:opacity-40"
                        style={{ color: 'var(--lodge-orange-600, var(--fv3-accent-ink, #ea580c))', background: 'var(--lodge-orange-50, var(--fv3-accent-wash, #fff7ed))' }}
                        title="Accept all accounts in this department"
                      >
                        Accept all
                      </button>
                      <button
                        onClick={() => setGroupAccepted(items, false)}
                        disabled={noneAccepted || isCommitting}
                        className="px-2 py-1 text-[11px] font-semibold rounded-md transition-colors disabled:opacity-40"
                        style={{ color: 'var(--lodge-onyx-600)', background: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))' }}
                        title="Reject all accounts in this department"
                      >
                        Reject all
                      </button>
                    </div>
                  </div>

                  {/* Rows */}
                  {!isCollapsed && items.map(p => {
                    const state = rowState[p.chartOfAccountId] || { accepted: true, editedInput: null };
                    const isExpanded = expandedRows.has(p.chartOfAccountId);
                    const badge = confidenceBadge(p);
                    const driverCfg = DRIVER_CONFIG[p.suggestion.driverType];
                    const percent = isPercentDriver(p.suggestion.driverType);
                    const inputValue = state.editedInput !== null
                      ? state.editedInput
                      : (p.suggestion.input !== null && p.suggestion.input !== undefined ? String(p.suggestion.input) : '');
                    const hasInput = p.suggestion.input !== null && p.suggestion.input !== undefined;

                    return (
                      <div
                        key={p.chartOfAccountId}
                        className="border-b last:border-b-0"
                        style={{
                          borderColor: 'var(--lodge-onyx-100, var(--fv3-n-50, #f1f5f9))',
                          opacity: state.accepted ? 1 : 0.55,
                        }}
                      >
                        <div className="flex items-center gap-3 px-3 py-2">
                          {/* Account */}
                          <button
                            onClick={() => toggleRowExpanded(p.chartOfAccountId)}
                            className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
                            title={isExpanded ? 'Hide explanation' : 'Show explanation'}
                          >
                            {isExpanded
                              ? <ChevronDown size={13} className="flex-shrink-0" style={{ color: 'var(--lodge-onyx-400)' }} />
                              : <ChevronRight size={13} className="flex-shrink-0" style={{ color: 'var(--lodge-onyx-400)' }} />}
                            <span className="text-xs tabular-nums flex-shrink-0" style={{ color: 'var(--lodge-onyx-500)' }}>
                              {p.accountNumber}
                            </span>
                            <span className="text-sm font-medium truncate" style={{ color: 'var(--lodge-onyx-800)' }}>
                              {p.accountName}
                            </span>
                          </button>

                          {/* Proposed driver */}
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-medium flex-shrink-0 ${driverCfg ? `${driverCfg.bgColor} ${driverCfg.color}` : 'bg-slate-100 text-slate-600'}`}
                            title={p.suggestion.driverType}
                          >
                            {driverCfg?.label ?? p.suggestion.driverType}
                          </span>

                          {/* Editable driver input */}
                          {hasInput ? (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!percent && (
                                <span className="text-[11px]" style={{ color: 'var(--lodge-onyx-400)' }}>$</span>
                              )}
                              <input
                                type="number"
                                value={inputValue}
                                step="any"
                                disabled={!state.accepted || isCommitting}
                                onChange={e => setEditedInput(p.chartOfAccountId, e.target.value)}
                                className="w-20 px-1.5 py-1 text-xs text-right tabular-nums rounded-md border outline-none disabled:cursor-not-allowed"
                                style={{
                                  borderColor: state.editedInput !== null ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-200, var(--fv3-n-200, #cbd5e1))',
                                  color: 'var(--lodge-onyx-800)',
                                  background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))',
                                }}
                                title="Override the proposed driver input"
                              />
                              {percent && (
                                <span className="text-[11px]" style={{ color: 'var(--lodge-onyx-400)' }}>%</span>
                              )}
                            </div>
                          ) : (
                            <span className="w-20 flex-shrink-0 text-center text-[11px]" style={{ color: 'var(--lodge-onyx-300, var(--fv3-n-200, #cbd5e1))' }}>—</span>
                          )}

                          {/* AI-reviewed marker (arbiter changed this pick) */}
                          {p.suggestion.reason?.startsWith('AI review:') && (
                            <span
                              className="flex items-center flex-shrink-0"
                              title={p.suggestion.reason}
                              style={{ color: 'var(--lodge-orange-500)' }}
                            >
                              <Sparkles size={12} />
                            </span>
                          )}

                          {/* Confidence badge */}
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium flex-shrink-0 ${badge.className}`}>
                            {badge.label}
                          </span>

                          {/* Annual total */}
                          <span className="w-24 text-right text-sm font-medium tabular-nums flex-shrink-0" style={{ color: 'var(--lodge-onyx-800)' }}>
                            {formatCurrency(p.annualTotal || 0, true, currency)}
                          </span>

                          {/* Accept / Reject toggle */}
                          <div
                            className="inline-flex items-center rounded-lg border p-0.5 flex-shrink-0"
                            style={{ borderColor: 'var(--lodge-onyx-200, var(--fv3-n-200, #cbd5e1))' }}
                          >
                            <button
                              onClick={() => setAccepted(p.chartOfAccountId, true)}
                              disabled={isCommitting}
                              className="px-1.5 py-0.5 rounded-md transition-colors"
                              style={state.accepted
                                ? { background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)' }
                                : { color: 'var(--lodge-onyx-400)' }}
                              title="Accept this driver"
                            >
                              <Check size={13} />
                            </button>
                            <button
                              onClick={() => setAccepted(p.chartOfAccountId, false)}
                              disabled={isCommitting}
                              className="px-1.5 py-0.5 rounded-md transition-colors"
                              style={!state.accepted
                                ? { background: 'var(--lodge-onyx-600, var(--fv3-n-600, #475569))', color: 'var(--fv3-n-0, #ffffff)' }
                                : { color: 'var(--lodge-onyx-400)' }}
                              title="Reject this driver"
                            >
                              <Ban size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Expanded reason / explanation */}
                        {isExpanded && (
                          <div
                            className="mx-3 mb-2 px-3 py-2 rounded-lg text-xs leading-relaxed"
                            style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}
                          >
                            {p.suggestion.reason && (
                              <p className="mb-1">
                                <span className="font-semibold" style={{ color: 'var(--lodge-onyx-700, var(--fv3-n-700, #334155))' }}>Why: </span>
                                {p.suggestion.reason}
                              </p>
                            )}
                            {p.explanation && <p>{p.explanation}</p>}
                            {typeof p.suggestion.correlation === 'number' && (
                              <p className="mt-1" style={{ color: 'var(--lodge-onyx-500)' }}>
                                Correlation: {p.suggestion.correlation.toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Needs manual setup */}
            {summary && summary.unassignedWithoutSuggestion.length > 0 && (
              <div
                className="rounded-xl border px-4 py-3"
                style={{ background: 'var(--lodge-white, var(--fv3-n-0, #ffffff))', borderColor: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Info size={14} style={{ color: 'var(--lodge-onyx-400)' }} />
                  <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--lodge-onyx-500)' }}>
                    Needs manual setup ({summary.unassignedWithoutSuggestion.length})
                  </h3>
                </div>
                <div className="space-y-1">
                  {summary.unassignedWithoutSuggestion.map(a => (
                    <div key={a.chartOfAccountId} className="flex items-baseline gap-2 text-xs" style={{ color: 'var(--lodge-onyx-500)' }}>
                      <span className="font-medium" style={{ color: 'var(--lodge-onyx-600)' }}>{a.accountName}</span>
                      <span className="truncate">{a.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {proposals.length === 0 && (
              <div className="text-center py-12 text-sm" style={{ color: 'var(--lodge-onyx-500)' }}>
                No driver proposals were generated for this version.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AiBuildReviewScreen;
